import { useEffect, useRef, useState } from 'react';
import { GameState } from '../api';

const fallbackHistory = new Map<string, Set<number>>();
const hasOpenDialog = () => !!document.querySelector('[role="dialog"][aria-modal="true"]');

function readHistory(key: string): Set<number> {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) ?? '[]');
    if (Array.isArray(value) && value.every(id => Number.isSafeInteger(id) && id > 0)) {
      return new Set([...value, ...Array.from(fallbackHistory.get(key) ?? [])]);
    }
  } catch { /* Storage can be disabled; retain history for this page session. */ }
  return new Set(fallbackHistory.get(key));
}

/** Controller-level presentation history, independent of investigator/turn selection. */
export function useLatestMythosPopup(state: GameState, playerName: string, mobile: boolean,
  loading: boolean, popupOpen: boolean, onOpen: () => void) {
  const controller = playerName.trim().toLowerCase();
  const key = `arkhamLatestMythosShown:${JSON.stringify([state.gameId, controller])}`;
  const sequence = state.latestMythosSequence;
  const sequenceId = sequence?.sequenceId;
  const eligible = mobile && !!state.gameId && !!controller &&
    !!state.players?.some(player => player.name.trim().toLowerCase() === controller) &&
    state.status !== 'Created' && state.status !== 'Finished' && !state.isFinalBattle &&
    !/final\s*battle/i.test(state.currentPhase ?? '') && !/^Mythos$/i.test(state.currentPhase ?? '') &&
    Number.isSafeInteger(sequenceId) && sequenceId! > 0 && !!sequence?.entries?.length &&
    sequence.entries.every(entry => entry.status === 'Resolved');
  const [historyVersion, setHistoryVersion] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(hasOpenDialog);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;
  const pending = eligible && !readHistory(key).has(sequenceId!);

  // Modal inspectors defer announcements; nonmodal Digital Actions stay underneath.
  // Closing animations clear their active attributes immediately.
  useEffect(() => {
    if (!pending) return;
    const update = () => setDialogOpen(hasOpenDialog());
    const observer = new MutationObserver(update);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-modal', 'aria-hidden'] });
    update();
    return () => observer.disconnect();
  }, [pending]);

  useEffect(() => {
    if (!pending || !popupOpen) return;
    const shown = readHistory(key);
    shown.add(sequenceId!);
    try {
      window.localStorage.setItem(key, JSON.stringify(Array.from(shown)));
    } catch {
      fallbackHistory.set(key, shown);
    }
    setHistoryVersion(version => version + 1);
  }, [pending, popupOpen, key, sequenceId]);

  useEffect(() => {
    if (!pending || loading || popupOpen || dialogOpen) return;
    // Wait for sibling effects (including game/layout resets) to commit first.
    const frame = requestAnimationFrame(() => {
      if (!hasOpenDialog()) openRef.current();
    });
    return () => cancelAnimationFrame(frame);
  }, [pending, loading, popupOpen, dialogOpen, key, sequenceId, historyVersion]);

  return pending;
}
